import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import {
	DeclarationOverviewStatus,
	DeclarationsOverview,
} from '../../../components/organisms/DeclarationsOverview/DeclarationsOverview';
import DeclarationOverviewItem from '../../../components/organisms/DeclarationsOverview/DeclarationOverviewItem';
import React, { useEffect, useState } from 'react';
import { MonthlyDeclarationOverviewItem } from '../../../@types/TaxProfile';
import PurchaseDeclaration from '../../../components/organisms/DeclarationsOverview/PurchaseDeclaration';
import {
	CaretDown,
	CaretUp,
	CheckCircle,
	CheckSquare,
	Files,
	Headset,
	ListMagnifyingGlass,
	Square,
	XCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { capitalizeText } from '../../../shared/utils/general.util';
import { SHORT_REGIME_NAME } from '../../../shared/constants/regimes';
import { sendEvent } from '../../../services/user-interaction.service';
import { ANALYTICS_EVENTS } from '../../../shared/constants/events';
import { useMediaQuery } from '@mui/material';

const groupDeclarationsByYear = (
	declarations?: MonthlyDeclarationOverviewItem[],
): { year: number; declarations: MonthlyDeclarationOverviewItem[] }[] => {
	if (!declarations || declarations.length === 0) {
		return [];
	}

	const yearMap: Record<number, MonthlyDeclarationOverviewItem[]> = {};

	declarations.forEach((declaration) => {
		const year = declaration.declaration_period
			? parseInt(declaration.declaration_period.split('-')[1])
			: new Date().getFullYear();

		if (!yearMap[year]) {
			yearMap[year] = [];
		}

		yearMap[year].push(declaration);
	});

	return Object.keys(yearMap)
		.map((yearStr) => {
			const year = parseInt(yearStr);
			return {
				year,
				declarations: yearMap[year],
			};
		})
		.sort((a, b) => b.year - a.year);
};

const Step3 = () => {
	const navigate = useNavigate();

	const [selectedDeclarations, setSelectedDeclarations] = useState<
		MonthlyDeclarationOverviewItem[]
	>([]);
	const [showAllDeclarations, setShowAllDeclarations] = useState(false);

	const taxpayer = useSelector((state: RootState) => state.taxProfile.taxPayer);
	const userProfile = useSelector((state: RootState) => state.profile.user);

	const fullName = taxpayer?.name
		? taxpayer?.name
		: `${userProfile?.first_name} ${userProfile?.father_surname}`;

	const userRegimes = taxpayer?.status?.tax_regimes
		?.map((regime) => {
			return SHORT_REGIME_NAME[Number(regime.code)];
		})
		?.reduce((result, regime, index, array) => {
			if (array.length === 1) return regime;
			if (array.length === 2)
				return index === 0 ? `${regime} y ` : `${result}${regime}`;
			return index === array.length - 1
				? `${result} y ${regime}`
				: index === 0
					? `${regime}, `
					: `${result}${regime}, `;
		}, '');

	const declarationsOverviewDetail = useSelector(
		(state: RootState) => state.tax.declarationsOverviewDetail,
	);

	const pendingDeclarations = declarationsOverviewDetail
		?.filter(
			(declaration) =>
				declaration.obligation_compliance_check ===
					DeclarationOverviewStatus.MISSING &&
				declaration.period_hired_with_heru !== 'Periodo contratado',
		)
		// User declarations whitelist
		?.filter((declaration) => {
			if ([463337]?.includes(userProfile?.id || 0)) {
				if (
					['2-2024', '1-2024']?.includes(declaration.declaration_period || '')
				)
					return false;
				return true;
			}
			return true;
		});

	const adquiredDeclarations = pendingDeclarations?.filter(
		(item) => item?.period_hired_with_heru === 'Periodo contratado',
	);

	const allDeclarationsAreAdquired =
		adquiredDeclarations?.length === pendingDeclarations?.length;

	const allDeclarationsAreSelected =
		selectedDeclarations?.length === pendingDeclarations?.length;

	const isSelectedAllYear = (year: number) => {
		return groupDeclarationsByYear(pendingDeclarations)
			?.find((y) => y.year === year)
			?.declarations?.every((d) =>
				selectedDeclarations.some(
					(sd) => sd.declaration_period === d.declaration_period,
				),
			);
	};

	const handleToggleYearSelection = (
		year: number,
		e: React.MouseEvent<HTMLDivElement>,
	) => {
		e.stopPropagation();
		const yearGroup = groupDeclarationsByYear(pendingDeclarations)?.find(
			(y) => y.year === year,
		);

		if (!yearGroup) return;

		const yearDeclarations = yearGroup.declarations;

		if (isSelectedAllYear(year)) {
			setSelectedDeclarations(
				selectedDeclarations.filter(
					(sd) =>
						!yearDeclarations.some(
							(d) => d.declaration_period === sd.declaration_period,
						),
				),
			);
		} else {
			setSelectedDeclarations([...selectedDeclarations, ...yearDeclarations]);
		}
	};

	const handleAddAllDeclarations = () => {
		if (allDeclarationsAreSelected) {
			setSelectedDeclarations([]);
		} else if (pendingDeclarations) {
			setSelectedDeclarations(pendingDeclarations);
			setShowAllDeclarations(true);
		}
	};

	const isMobile = useMediaQuery('(max-width: 768px)');

	useEffect(() => {
		sendEvent({
			user_id: userProfile?.id,
			event: ANALYTICS_EVENTS.IMSS_SECTION_TAB_REGULARIZATION,
			source: isMobile ? 'app' : 'web',
			event_payload: {},
		});
	}, []);

	if (
		pendingDeclarations?.length === 0 &&
		declarationsOverviewDetail &&
		declarationsOverviewDetail?.length > 0
	) {
		return (
			<div className='flex flex-col gap-2'>
				<div className='bg-white rounded-xl p-4 flex flex-col gap-6 relative'>
					<div className='flex flex-col justify-center md:items-center'>
						<CheckCircle
							weight='duotone'
							size={24}
							className='text-h-success-50 mb-2'
						/>
						<p className='font-medium text-lg'>
							Estás al día con tus declaraciones
						</p>
						<p className='text-neutral-500 font-light'>
							No tienes declaraciones pendientes por presentar
						</p>
					</div>
				</div>
				<DeclarationsOverview />
			</div>
		);
	}
	if (pendingDeclarations && pendingDeclarations?.length > 0) {
		return (
			<div className='bg-white rounded-xl p-4 flex flex-col gap-4 relative'>
				<div className='flex flex-col gap-2 justify-center md:items-center'>
					<Files
						weight='duotone'
						size={24}
						className='text-h-primary mb-2'
					/>
					<p className='font-medium text-lg'>Mantén tu perfil fiscal al día</p>
					<p className='text-neutral-500 font-light text-left md:text-center'>
						El gobierno nacional creó esta ley junto con el IMSS y el SAT. Para
						aprovechar sus beneficios, te recomendamos tener tu perfil fiscal
						completo.
					</p>
				</div>

				<div className='bg-neutral-100/70 p-4 rounded-lg my-2'>
					<p className='font-medium text-base'>{capitalizeText(fullName)}</p>
					<div className='mt-3 flex flex-col gap-2'>
						{[
							{
								icon: CheckCircle,
								label: `RFC: ${taxpayer?.code}`,
							},
							{
								icon: CheckCircle,
								label: `Estás en el régimen de ${userRegimes}`,
							},
							{
								icon: (
									<XCircle
										size={20}
										weight='fill'
										className='text-red-600 min-w-5 min-h-5'
									/>
								),
								label: `Tienes ${pendingDeclarations?.length} declaraciones ${
									pendingDeclarations && pendingDeclarations.length > 0
										? `de ${Array.from(
												new Set(
													pendingDeclarations.map(
														(d) => d.declaration_period?.split('-')[1],
													),
												),
											)
												.sort((a, b) => (b || '').localeCompare(a || ''))
												.join(', ')} `
										: ''
								} pendientes ante el SAT`,
							},
						]
							?.filter(
								(item) =>
									!(
										item.label === 'Estás en' &&
										taxpayer?.status?.tax_regimes?.length === 0
									),
							)
							?.map((item, index) => {
								return (
									<div
										key={index}
										className='flex items-center gap-2'
									>
										{React.isValidElement(item.icon)
											? item.icon
											: React.createElement(item.icon as any, {
													weight: 'fill',
													size: 20,
													className: 'text-green-600 min-w-5 min-h-5',
												})}
										<p className='font-light'>{item.label}</p>
									</div>
								);
							})}
					</div>
				</div>

				{!allDeclarationsAreAdquired && (
					<div className='text-sm leading-tight tracking-tight bg-h-warning-90 text-h-warning-10 w-full rounded-lg p-2 hidden'>
						<b>
							Tienes {pendingDeclarations?.length} declaraciones pendientes,{' '}
							{pendingDeclarations && pendingDeclarations.length > 0
								? `de ${Array.from(
										new Set(
											pendingDeclarations.map(
												(d) => d.declaration_period?.split('-')[1],
											),
										),
									)
										.sort((a, b) => (b || '').localeCompare(a || ''))
										.join(', ')} `
								: ''}
							pendientes por presentar ante el SAT
						</b>
						. esto podría generar contratiempos y afectar los beneficios que
						obtienes con esta nueva regulación IMSS.
					</div>
				)}

				<div>
					{pendingDeclarations?.length > 1 && (
						<button
							onClick={handleAddAllDeclarations}
							className={`h-8 w-full border text-h-primary-40 border-h-primary-70 rounded-lg mb-2 flex items-center justify-between px-2 gap-2 font-medium ${allDeclarationsAreSelected ? 'bg-h-primary-95' : ''}`}
						>
							{allDeclarationsAreSelected
								? 'Todas seleccionadas'
								: 'Regularizar todas con descuento'}
							{allDeclarationsAreSelected ? (
								<CheckSquare
									weight='fill'
									size={16}
									className='text-h-primary-50'
								/>
							) : (
								<Square
									weight='bold'
									size={16}
								/>
							)}
						</button>
					)}
					<div className='flex flex-col gap-4'>
						{groupDeclarationsByYear(pendingDeclarations)
							?.slice(0, showAllDeclarations ? undefined : 2)
							?.map((year) => {
								return (
									<div
										className='cursor-pointer flex flex-col gap-2 shadow-sm border border-neutral-100 rounded-lg p-2 hover:md:translate-y-[-1px] hover:md:shadow-md transition-all duration-200'
										onClick={(e) => handleToggleYearSelection(year?.year, e)}
									>
										<div
											className={`font-medium text-lg flex justify-between gap-2`}
										>
											Tus declaraciones pendientes del {year?.year}{' '}
											<div>
												{isSelectedAllYear(year?.year) ? (
													<CheckSquare
														weight='fill'
														size={16}
														className='text-h-primary-50'
													/>
												) : (
													<Square
														weight='bold'
														size={16}
													/>
												)}
											</div>
										</div>
										<div className='grid grid-cols-3 md:grid-cols-4 gap-2'>
											{year?.declarations?.map((declaration, index, array) => (
												<DeclarationOverviewItem
													showIncomes={false}
													key={index}
													declaration={declaration}
													index={index}
													array={array}
													selectedDeclarations={selectedDeclarations}
													setSelectedDeclarations={setSelectedDeclarations}
													setSelectedPresentedDeclaration={() => {}}
													variant='condensed'
												/>
											))}
										</div>
									</div>
								);
							})}
					</div>
					{groupDeclarationsByYear(pendingDeclarations)?.length > 2 && (
						<div className='flex justify-center mt-4'>
							<button
								onClick={() => {
									setShowAllDeclarations(!showAllDeclarations);
									if (showAllDeclarations) {
										setSelectedDeclarations([]);
									}
								}}
								className={`px-4 py-2  w-full rounded-lg font-medium  transition-colors flex items-center justify-center gap-2 ${
									showAllDeclarations
										? 'text-neutral-900 border border-neutral-200 hover:bg-neutral-50'
										: 'bg-neutral-50 text-neutral-900 hover:bg-blue-100'
								}`}
							>
								{showAllDeclarations ? 'Mostrar menos' : 'Ver todas'}
								{showAllDeclarations ? (
									<CaretUp
										weight='bold'
										size={16}
									/>
								) : (
									<CaretDown
										weight='bold'
										size={16}
									/>
								)}
							</button>
						</div>
					)}
				</div>

				<hr />

				<div
					className='leading-tight tracking-tight rounded-lg flex flex-col gap-1 items-start'
					onClick={() => navigate(ROUTES.SUPPORT)}
				>
					<p className='font-medium flex items-center gap-1'>
						<Headset weight='bold' /> ¿Necesitas ayuda?
					</p>
					<p className='font-light'>
						Contáctanos para recibir asistencia personalizada sobre tus
						declaraciones pendientes.
					</p>
					<button className='underline'>Contactar a soporte</button>
				</div>

				<PurchaseDeclaration
					selectedDeclarations={selectedDeclarations}
					setSelectedDeclarations={setSelectedDeclarations}
					resetOrders={true}
					variant='blue'
				/>
			</div>
		);
	}

	return (
		<div className='bg-white rounded-xl p-4 flex flex-col gap-6 relative'>
			<div className='flex flex-col justify-center md:items-center text-center'>
				<ListMagnifyingGlass
					weight='duotone'
					size={24}
					className='text-h-primary mb-2'
				/>
				<p className='font-medium text-lg mb-2'>
					Estamos trabajando para que puedas ver el estado de tus declaraciones
				</p>
				<p className='text-neutral-500 font-light'>
					Vuelve más tarde para ver toda la información.
				</p>
			</div>
		</div>
	);
};

export default Step3;
